// IMPORTANT: must stay the first import — installs Sentry's global handlers
// before any app code can throw.
import '@/instrument'
// The brand's typefaces, self-hosted: Inter for the interface, JetBrains Mono for
// times, dimensions and counts. General Sans, for headings, is declared in
// `index.css` from the file Fontshare ships (it is not on Fontsource).
import '@fontsource-variable/inter'
import '@fontsource-variable/jetbrains-mono'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { AppErrorFallback } from '@/components/AppErrorFallback'
import { AuthProvider } from '@/auth/AuthContext'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ToastProvider } from '@/components/ui/toast'
import { UpdateAvailableBanner } from '@/components/UpdateAvailableBanner'
import { router } from '@/router'
import '@/index.css'

/**
 * A single QueryClient for the app. `retry: 1` keeps failed reads from hammering
 * the API, and a short `staleTime` is set per-query in the hooks.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element #root was not found in index.html')
}

createRoot(rootElement).render(
  <StrictMode>
    {/* Outermost so a provider that throws is reported and still renders
        something, rather than leaving a blank page. */}
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          {/* Outside AuthProvider — deploy detection does not need a session. */}
          <UpdateAvailableBanner />
          <AuthProvider>
            <ToastProvider>
              <TooltipProvider>
                <RouterProvider router={router} />
              </TooltipProvider>
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
